"use client";
import WebsiteIcon from "@/icons/WebsiteIcon";
import { hasWebsiteIcon, websiteIcon } from "@/icons/icon-catalogue";

import type { ResolvedHeaderAction } from "@/utils/header-actions";
import { surfaceStyle, typographyStyle } from "@/utils/visual-style";
import { headerActionGeometryStyle } from "@/utils/header-action-style";
import { openContentDialog } from "@/components/ui/ContentDialog";
import NavigationLink from "./NavigationLink";
import NavIcon, { type NavIconName } from "./NavIcon";

export interface HeaderActionIcons {
  search?: React.ReactNode;
  user?: React.ReactNode;
}

const typeIcon: Partial<Record<ResolvedHeaderAction["type"], NavIconName>> = {
  search: "search",
  login: "user",
  phone: "phone",
};

/** One header action; `placement` decides whether it renders as in the bar, the menu or the bottom bar. */
export default function HeaderActionItem({
  action,
  placement,
  icons,
  onSearch,
  onNavigate,
}: {
  action: ResolvedHeaderAction;
  placement: "bar" | "menu" | "bottom";
  icons?: HeaderActionIcons;
  onSearch?: () => void;
  onNavigate?: () => void;
}) {
  const { config } = action;
  const iconName = typeIcon[action.type];
  const customIcon = hasWebsiteIcon(config.iconId);
  const visibleIcon = customIcon ? !!websiteIcon(config.iconId) : !!iconName;
  const iconOnly =
    action.style === "icon" &&
    visibleIcon &&
    (customIcon || placement === "bar");
  const style =
    placement === "bottom"
      ? "bottom"
      : iconOnly
        ? "icon"
        : action.style === "icon"
          ? "plain"
          : action.style;
  const ownIcon =
    action.type === "search"
      ? icons?.search
      : action.type === "login"
        ? icons?.user
        : undefined;
  const icon = customIcon ? (
    <WebsiteIcon name={config.iconId} className="size-[1em] shrink-0" />
  ) : iconName ? (
    (ownIcon ?? <NavIcon name={iconName} />)
  ) : null;
  const geometryStyle = headerActionGeometryStyle(config);
  const hasWidth =
    "--re-nav-width" in geometryStyle || "--re-nav-width-desktop" in geometryStyle;
  const hasHeight =
    "--re-nav-height" in geometryStyle || "--re-nav-height-desktop" in geometryStyle;
  const className = [
    "re-nav-action",
    `re-nav-action-${style}`,
    style === "primary" && "re-action re-action-pill",
    style === "text" && "re-action re-action-quiet",
    hasWidth && "re-nav-action-has-width",
    hasHeight && "re-nav-action-has-height",
    config.surface && ["primary", "outline"].includes(style) && "re-surface",
  ]
    .filter(Boolean)
    .join(" ");
  const css = {
    color: config.textColor,
    backgroundColor:
      config.surface || !["primary", "outline", "bottom"].includes(style)
        ? undefined
        : config.bgColor,
    borderColor: config.borderColor,
    ...surfaceStyle(config.surface, config.bgColor),
    ...typographyStyle(config.typography),
    ...geometryStyle,
    "--re-action-hover-bg": config.hoverBgColor,
    "--re-action-hover-color": config.hoverTextColor,
    "--re-action-hover-border": config.hoverBorderColor,
  } as React.CSSProperties;
  // Keep base colours inline, but let explicitly configured hover states replace them.
  // Surface backgrounds also need the state variable because their selector outranks .re-action:hover.
  if (config.hoverTextColor)
    css.color = `var(--re-nav-action-current-color, ${
      config.textColor ??
      (style === "primary" ? "var(--button-text-color)" : "inherit")
    })`;
  if (config.hoverBgColor) {
    if (config.surface)
      (css as Record<string, unknown>)["--re-surface-color"] =
        `var(--re-nav-action-current-bg, ${(css as Record<string, unknown>)["--re-surface-color"]})`;
    else if (css.backgroundColor)
      css.backgroundColor = `var(--re-nav-action-current-bg, ${css.backgroundColor})`;
  }
  if (config.hoverBorderColor)
    css.borderColor = `var(--re-nav-action-current-border, ${
      css.borderColor ?? "currentColor"
    })`;
  const props = {
    className,
    style: css,
    "aria-label": iconOnly ? action.label : undefined,
    "data-mobile": placement === "bar" ? action.mobile : undefined,
  };
  const content = iconOnly ? (
    icon
  ) : (
    <>
      {config.iconPosition !== "after" &&
        (customIcon || action.type !== "link") &&
        icon}
      <span>{action.label}</span>
      {config.iconPosition === "after" &&
        (customIcon || action.type !== "link") &&
        icon}
    </>
  );
  if (action.type === "search")
    return (
      <button
        type="button"
        {...props}
        onClick={() => {
          onNavigate?.();
          onSearch?.();
        }}
      >
        {content}
      </button>
    );
  if (action.dialogTargetId)
    return (
      <button
        type="button"
        {...props}
        onClick={() => {
          onNavigate?.();
          openContentDialog(action.dialogTargetId!);
        }}
      >
        {content}
      </button>
    );
  return (
    <NavigationLink link={action.link!} onClick={onNavigate} {...props}>
      {content}
    </NavigationLink>
  );
}
